import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import permissionService from '../../../services/permissionService';
import './AssignPermissions.css';

const AssignPermissions = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();

  const [role, setRole] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);

  const fetchRolePermissions = useCallback(async () => {
    try {
      setFetching(true);
      setError(null);
      const response = await permissionService.getRolePermissions(roleId);

      if (response.success) {
        setRole(response.data.role);
        setPages(response.data.pages || []);
        setSelectedPermissions(response.data.permissionIds || []);
      } else {
        setError(response.message || 'Failed to fetch role permissions');
        toast.error(response.message || 'Failed to fetch role permissions');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch role permissions';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setFetching(false);
    }
  }, [roleId]);

  useEffect(() => {
    fetchRolePermissions();
  }, [fetchRolePermissions]);


  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedPermissions.length === 0) {
      toast.warning('Please select at least one permission');
      return;
    }

    try {
      setLoading(true);
      const response = await permissionService.assignRolePermissions(roleId, selectedPermissions);

      if (response.success) {
        toast.success('Role permissions assigned successfully');
        navigate('/permission/roles');
      } else {
        toast.error(response.message || 'Failed to assign permissions');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to assign permissions';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/permission/roles');
  };

  if (fetching) {
    return (
      <div className="assign-permissions-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assign-permissions-page">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={handleCancel} className="back-btn">Back to Roles</button>
        </div>
      </div>
    );
  }

  return (
    <div className="assign-permissions-page">
      <div className="permission-card-container">
        <div className="card-header">
          <h2 className="card-title">
            Assign Role & Permissions To {role?.role_name}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="assign-permissions-form">
          <div className="permissions-grid-wrapper">
            <div className="permissions-grid">
              {pages.map((page) => {
                return (
                  <div key={page.id} className="permission-box">
                    <div className="permission-box-header">
                      <h3>{page.name}</h3>
                    </div>
                    <div className="permission-list">
                      {page.permissions && page.permissions.length > 0 ? (
                        page.permissions.map((permission) => (
                          <div key={permission.id} className="permission-item">
                            <label className="permission-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(permission.id)}
                                onChange={() => handlePermissionToggle(permission.id)}
                                className="custom-checkbox"
                              />
                              <span className="checkbox-text">{permission.name}</span>
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="no-permissions">No permissions available</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {pages.length === 0 && (
            <div className="no-pages">
              <p>No pages available for this role</p>
            </div>
          )}

          <div className="form-actions-right">
            <button type="button" onClick={handleCancel} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={loading}>
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignPermissions;

